/**
 * useRelationshipChat Hook
 *
 * React Query hooks for relationship tracker AI chat sessions.
 * Provides chat creation, fetching, and streaming message support.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createChat,
  deleteChat,
  getChat,
  sendMessageStreaming,
} from "../services/relationshipChat";
import type {
  RelationshipChat,
  RelationshipChatCreate,
  RelationshipChatMessage,
} from "../types/relationshipChat";

/**
 * Fetch a chat session with messages
 */
export function useRelationshipChat(chatId: string | null) {
  return useQuery<RelationshipChat, Error>({
    queryKey: ["relationshipChat", chatId],
    queryFn: () => getChat(chatId!),
    enabled: !!chatId,
  });
}

/**
 * Create a new chat session
 */
export function useCreateRelationshipChat() {
  const queryClient = useQueryClient();

  return useMutation<RelationshipChat, Error, RelationshipChatCreate>({
    mutationFn: createChat,
    onSuccess: (chat) => {
      queryClient.setQueryData(["relationshipChat", chat.id], chat);
    },
  });
}

/**
 * Delete a chat session
 */
export function useDeleteRelationshipChat() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: deleteChat,
    onSuccess: (_data, chatId) => {
      queryClient.removeQueries({ queryKey: ["relationshipChat", chatId] });
    },
  });
}

interface StreamingState {
  isStreaming: boolean;
  streamingContent: string;
  error: string | null;
}

/**
 * Streaming chat message hook for relationship tracker
 */
export function useStreamingRelationshipChat(chatId: string | null) {
  const queryClient = useQueryClient();
  const [streamingState, setStreamingState] = useState<StreamingState>({
    isStreaming: false,
    streamingContent: "",
    error: null,
  });
  const abortRef = useRef(false);
  const isStreamingRef = useRef(false);

  // Keep ref in sync with state so the guard check in sendMessage
  // always reads the latest value without a stale closure.
  useEffect(() => {
    isStreamingRef.current = streamingState.isStreaming;
  }, [streamingState.isStreaming]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!chatId || isStreamingRef.current) return;

      abortRef.current = false;
      setStreamingState({
        isStreaming: true,
        streamingContent: "",
        error: null,
      });

      try {
        await sendMessageStreaming(
          chatId,
          { content },
          {
            onUserMessage: (message: RelationshipChatMessage) => {
              // Add user message to cache optimistically
              queryClient.setQueryData<RelationshipChat>(
                ["relationshipChat", chatId],
                (old) => {
                  if (!old) return old;
                  return {
                    ...old,
                    messages: [...old.messages, message],
                  };
                }
              );
            },
            onContent: (chunk: string) => {
              if (abortRef.current) return;
              setStreamingState((prev) => ({
                ...prev,
                streamingContent: prev.streamingContent + chunk,
              }));
            },
            onDone: (message: RelationshipChatMessage) => {
              // Add assistant message to cache before clearing streaming
              queryClient.setQueryData<RelationshipChat>(
                ["relationshipChat", chatId],
                (old) => {
                  if (!old) return old;
                  return {
                    ...old,
                    messages: [...old.messages, message],
                  };
                }
              );
              setStreamingState({
                isStreaming: false,
                streamingContent: "",
                error: null,
              });
            },
            onError: (error: string) => {
              setStreamingState({
                isStreaming: false,
                streamingContent: "",
                error,
              });
            },
          }
        );
      } catch (err) {
        setStreamingState({
          isStreaming: false,
          streamingContent: "",
          error: err instanceof Error ? err.message : "Failed to send message",
        });
      }
    },
    [chatId, queryClient]
  );

  const cancelStream = useCallback(() => {
    abortRef.current = true;
    setStreamingState({
      isStreaming: false,
      streamingContent: "",
      error: null,
    });
  }, []);

  return {
    ...streamingState,
    sendMessage,
    cancelStream,
  };
}
